import type { WorkoutSessionDetails } from '../dto/WorkoutSessionDetails';
import { RoutineError, toRoutineError } from '../errors/RoutineError';
import type { Clock } from '../ports/Clock';
import type { WorkoutPersistenceTransactions } from '../ports/WorkoutPersistenceTransactions';
import type { WorkoutRepository } from '../ports/WorkoutRepository';
import { WorkoutSessionStatus } from '../../domain/workout/enums';
import { fromExerciseLogData, toExerciseLogData } from '../../domain/workout/WorkoutContent';
import { ExerciseStatus } from '../../domain/workout/enums';
import type { ExerciseLog } from '../../domain/workout/ExerciseLog';
import type { ProfileId } from '../../domain/shared/ids';
import { failure, success, type Result } from '../../domain/shared/Result';

/**
 * Applies "réduire / arrêter" (SPORT_ENGINE.md, MVP_IMPLEMENTATION_PLAN.md L7): every exercise
 * still pending is marked skipped and the session is closed as `reduced`, never as `completed`,
 * so the history preserves that the session was cut short rather than finished as prescribed.
 */
export class ReduceWorkoutSession {
  public constructor(
    private readonly workouts: WorkoutRepository,
    private readonly transactions: WorkoutPersistenceTransactions,
    private readonly clock: Clock,
  ) {}

  public async execute(
    profileId: ProfileId,
    logicalDate: string,
  ): Promise<Result<WorkoutSessionDetails, RoutineError>> {
    try {
      const session = await this.workouts.findByLogicalDate(profileId, logicalDate);
      if (!session) {
        return failure(new RoutineError('workout_not_found', 'Séance de sport introuvable.'));
      }
      const exercises = await this.transactions.listExercises(session.id);
      const timestamp = this.clock.now().toISOString();
      const updatedExercises = exercises.map<ExerciseLog>((exercise) => {
        const content = fromExerciseLogData(exercise.data);
        if (!content || content.status !== ExerciseStatus.pending) return exercise;
        return {
          ...exercise,
          data: toExerciseLogData({ ...content, status: ExerciseStatus.skipped }),
          updatedAt: timestamp,
        };
      });
      const updatedSession = {
        ...session,
        status: WorkoutSessionStatus.reduced,
        updatedAt: timestamp,
      };
      await this.transactions.updateSessionAndExercises(updatedSession, updatedExercises);
      return success({ session: updatedSession, exercises: updatedExercises });
    } catch (error) {
      return failure(toRoutineError(error));
    }
  }
}
